import { useState, useEffect, useCallback, useMemo, startTransition, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChart3,
  Calendar,
  Loader2,
  RefreshCw,
  Users,
  ShoppingBag,
} from "lucide-react";
import { formatCstDate } from "../utils/cstDate";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from "recharts";
import DateRangePicker from "../components/ui/date-range-picker";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  fetchSalesAnalytics,
  fetchSalesRestaurants,
  setSalesAnalyticsFilters,
} from "../Store/actions/salesAnalytics";
import {
  selectSalesAnalyticsActiveCacheKey,
  selectSalesAnalyticsData,
  selectSalesAnalyticsLoading,
  selectSalesRestaurants,
  selectSalesRestaurantsLoaded,
  selectSalesRestaurantsLoading,
} from "../Store/selectors/salesAnalytics";
import {
  buildSalesAnalyticsCacheKey,
  getDefaultRestaurant,
} from "../utils/salesAnalytics";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatNumber = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value || 0);

const formatBusinessDate = (value) => {
  if (!value || String(value).length !== 8) return value;
  const str = String(value);
  return `${str.slice(4, 6)}/${str.slice(6, 8)}/${str.slice(0, 4)}`;
};

const GROUP_BY_OPTIONS = [
  { value: "", label: "None" },
  { value: "REVENUE_CENTER", label: "Revenue Center" },
  { value: "DINING_OPTION", label: "Dining Option" },
  { value: "ORDER_SOURCE", label: "Order Source" },
];

const ORDER_SOURCE_LABELS = {
  IN_STORE: "In Store",
  API: "Third-Party / Online Ordering",
  ONLINE: "Online Ordering",
  KIOSK: "Kiosk",
  CALL_IN: "Call-In",
  CATERING: "Catering",
};

const ORDER_SOURCE_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4"];
const EMPLOYEE_CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4", "#ec4899", "#64748b"];

const formatOrderSourceLabel = (source) => {
  if (!source) return "Unknown";
  if (ORDER_SOURCE_LABELS[source]) return ORDER_SOURCE_LABELS[source];
  return source
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatPercent = (value) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(value || 0);

const MetricCard = memo(function MetricCard({ title, value, subtitle, valueClassName = "text-gray-900" }) {
  return (
    <Card className="border border-gray-200 bg-white shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-gray-500">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <p className={`text-2xl font-bold ${valueClassName}`}>{value}</p>
        {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
      </CardContent>
    </Card>
  );
});

export default function SalesAnalytics() {
  const dispatch = useDispatch();

  const restaurants = useSelector(selectSalesRestaurants);
  const restaurantsLoading = useSelector(selectSalesRestaurantsLoading);
  const restaurantsLoaded = useSelector(selectSalesRestaurantsLoaded);
  const analytics = useSelector(selectSalesAnalyticsData);
  const loading = useSelector(selectSalesAnalyticsLoading);
  const activeCacheKey = useSelector(selectSalesAnalyticsActiveCacheKey);

  const dateRange = useSelector((state) => state.salesAnalytics?.dateRange);
  const selectedRestaurantIds = useSelector(
    (state) => state.salesAnalytics?.selectedRestaurantIds || []
  );
  const groupBy = useSelector((state) => state.salesAnalytics?.groupBy || "");
  const chartView = useSelector((state) => state.salesAnalytics?.chartView || "bar");

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  useEffect(() => {
    if (!restaurantsLoaded) {
      dispatch(fetchSalesRestaurants());
    }
  }, [dispatch, restaurantsLoaded]);

  const loadSalesAnalytics = useCallback(
    ({ forceRefresh = false, showRefreshToast = false } = {}) => {
      if (!selectedRestaurantIds.length || !dateRange?.start || !dateRange?.end) {
        return;
      }

      const startDate = formatCstDate(dateRange.start);
      const endDate = formatCstDate(dateRange.end);
      const cacheKey = buildSalesAnalyticsCacheKey({
        startDate,
        endDate,
        restaurantIds: selectedRestaurantIds,
        groupBy,
      });

      if (!forceRefresh && activeCacheKey === cacheKey && analytics) {
        return;
      }

      startTransition(() => {
        dispatch(
          fetchSalesAnalytics({
            startDate,
            endDate,
            restaurantIds: selectedRestaurantIds,
            groupBy,
            forceRefresh,
            showRefreshToast,
          })
        );
      });
    },
    [activeCacheKey, analytics, dateRange, dispatch, groupBy, selectedRestaurantIds]
  );

  useEffect(() => {
    loadSalesAnalytics({ forceRefresh: false });
  }, [loadSalesAnalytics]);

  const summary = analytics?.summary;
  const records = useMemo(() => analytics?.records ?? [], [analytics?.records]);
  const orderSourceBreakdown = useMemo(
    () => analytics?.orderSourceBreakdown ?? [],
    [analytics?.orderSourceBreakdown]
  );
  const employeePerformance = useMemo(
    () => analytics?.employeePerformance ?? [],
    [analytics?.employeePerformance]
  );

  const employeeChartData = useMemo(
    () =>
      employeePerformance
        .filter((row) => row.netSales > 0)
        .slice(0, 10)
        .map((row) => ({
          name: row.name,
          netSales: row.netSales,
          hours: row.hours,
        })),
    [employeePerformance]
  );

  const chartData = useMemo(() => {
    const grouped = records.reduce((acc, row) => {
      const key = formatBusinessDate(row.businessDate);
      if (!acc[key]) {
        acc[key] = { date: key, netSales: 0, grossSales: 0, orders: 0, guests: 0 };
      }
      acc[key].netSales += row.netSalesAmount || 0;
      acc[key].grossSales += row.grossSalesAmount || 0;
      acc[key].orders += row.ordersCount || 0;
      acc[key].guests += row.guestCount || 0;
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => {
      const [am, ad, ay] = a.date.split("/");
      const [bm, bd, by] = b.date.split("/");
      return new Date(`${ay}-${am}-${ad}`) - new Date(`${by}-${bm}-${bd}`);
    });
  }, [records]);

  const tableColSpan = useMemo(() => {
    let cols = 8;
    if (groupBy) cols += 1;
    return cols;
  }, [groupBy]);

  const defaultRestaurant = useMemo(() => getDefaultRestaurant(restaurants), [restaurants]);

  const handleDateRangeApply = (startDate, endDate, label) => {
    dispatch(
      setSalesAnalyticsFilters({
        dateRange: { start: startDate, end: endDate, label },
      })
    );
    setIsDatePickerOpen(false);
  };

  const showInitialLoader =
    (loading || restaurantsLoading) && !analytics && !restaurantsLoaded;

  if (showInitialLoader) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 bg-gray-50 p-6">
        <Loader2 className="h-10 w-10 animate-spin text-amber-500" />
        <p className="text-sm text-gray-600">Loading sales analytics...</p>
      </div>
    );
  }

  const rangeLabel = dateRange?.label || "Selected range";

  return (
    <div className="relative flex flex-col bg-gray-50 p-4 sm:p-6">
      {loading && analytics && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center pt-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 shadow-sm">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Updating…
          </span>
        </div>
      )}

      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">Sales Analytics</h1>
        <p className="mt-1 text-sm text-gray-600 sm:text-base">
          Toast POS sales overview for {rangeLabel}
        </p>
      </div>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {defaultRestaurant ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <span className="shrink-0 text-sm font-medium text-gray-700">Restaurant</span>
            <span className="rounded-full border border-gray-700 bg-gray-700 px-3 py-1 text-sm text-white">
              {defaultRestaurant.restaurantName?.trim()}
            </span>
          </div>
        ) : (
          <div />
        )}
        <Button
          onClick={() => loadSalesAnalytics({ forceRefresh: true, showRefreshToast: true })}
          disabled={loading || !selectedRestaurantIds.length}
          className="shrink-0 bg-yellow-400 text-black hover:bg-yellow-500 sm:ml-auto"
        >
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Refresh
        </Button>
      </div>

      <Card className="mb-6 border border-gray-200 bg-white shadow-sm">
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Date Range</label>
              <Button
                variant="outline"
                onClick={() => setIsDatePickerOpen(true)}
                className="w-full justify-start text-left font-normal"
              >
                <Calendar className="mr-2 h-4 w-4 shrink-0" />
                {rangeLabel}
              </Button>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Group By</label>
              <select
                value={groupBy}
                onChange={(e) =>
                  dispatch(setSalesAnalyticsFilters({ groupBy: e.target.value }))
                }
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                {GROUP_BY_OPTIONS.map((option) => (
                  <option key={option.value || "none"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Toast Account</label>
              <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">
                {analytics?.meta?.credentialsSet || "Connected"}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Records</label>
              <p className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-800">
                {records.length} row{records.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {isDatePickerOpen && dateRange && (
        <DateRangePicker
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
          onApply={handleDateRangeApply}
          initialStartDate={dateRange.start}
          initialEndDate={dateRange.end}
        />
      )}

      {!analytics && loading ? (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 py-16">
          <Loader2 className="h-10 w-10 animate-spin text-amber-500" />
          <p className="text-sm font-medium text-gray-600">
            Updating sales, labor, and order source data...
          </p>
        </div>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              title="Net Sales"
              value={formatCurrency(summary?.netSales)}
              subtitle="Excludes tax, tips & discounts"
              valueClassName="text-emerald-600"
            />
            <MetricCard
              title="Gross Sales"
              value={formatCurrency(summary?.grossSales)}
              subtitle="Before discounts & refunds"
              valueClassName="text-blue-600"
            />
            <MetricCard
              title="Orders"
              value={formatNumber(summary?.orders)}
              subtitle={`Avg ${formatCurrency(summary?.avgOrderValue)} per order`}
            />
            <MetricCard
              title="Guests"
              value={formatNumber(summary?.guests)}
              subtitle={`${formatNumber(summary?.laborHours)} labor hours`}
            />
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard title="Discounts" value={formatCurrency(summary?.discounts)} valueClassName="text-rose-600" />
            <MetricCard title="Voids" value={formatCurrency(summary?.voids)} valueClassName="text-orange-600" />
            <MetricCard title="Refunds" value={formatCurrency(summary?.refunds)} valueClassName="text-red-600" />
            <MetricCard
              title="Sales / Labor Hour"
              value={formatCurrency(summary?.salesPerLaborHour)}
              subtitle={`Labor pay ${formatCurrency(summary?.laborPay)}`}
            />
          </div>

          <Card className="mb-6 border border-gray-200 bg-white shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-lg font-semibold text-gray-800">Sales Trend</CardTitle>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={chartView === "bar" ? "default" : "outline"}
                  onClick={() => dispatch(setSalesAnalyticsFilters({ chartView: "bar" }))}
                  className={chartView === "bar" ? "bg-gray-800 text-white hover:bg-gray-700" : ""}
                >
                  Bar
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={chartView === "line" ? "default" : "outline"}
                  onClick={() => dispatch(setSalesAnalyticsFilters({ chartView: "line" }))}
                  className={chartView === "line" ? "bg-gray-800 text-white hover:bg-gray-700" : ""}
                >
                  Line
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-72 sm:h-80">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    {chartView === "bar" ? (
                      <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} width={70} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="netSales" name="Net Sales" fill="#10b981" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                        <Bar dataKey="grossSales" name="Gross Sales" fill="#3b82f6" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                      </BarChart>
                    ) : (
                      <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} width={70} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Line type="monotone" dataKey="netSales" name="Net Sales" stroke="#10b981" strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="orders" name="Orders" stroke="#8b5cf6" strokeWidth={2} dot={false} isAnimationActive={false} />
                      </LineChart>
                    )}
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-gray-500">
                    No sales data for the selected range
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
            <Card className="border border-gray-200 bg-white shadow-sm">
              <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-4">
                <ShoppingBag className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-lg font-semibold text-gray-800">Top Performing Order Sources</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto p-0 sm:px-6 sm:pb-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Order Source</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Sales</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Orders</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Share</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {orderSourceBreakdown.map((row) => (
                      <tr key={row.orderSource} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-800">
                          {row.displayName || formatOrderSourceLabel(row.orderSource)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-emerald-700">
                          {formatCurrency(row.sales)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                          {formatNumber(row.orders)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                          {formatPercent(row.percentage)}%
                        </td>
                      </tr>
                    ))}
                    {!orderSourceBreakdown.length && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">
                          No order source data for this range
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {orderSourceBreakdown.some((row) => row.orderSource === "API") && (
                  <p className="mt-3 px-4 text-xs text-gray-500 sm:px-0">
                    &quot;Third-Party / Online Ordering&quot; includes orders from delivery apps and online integrations (Toast API channel).
                  </p>
                )}
              </CardContent>
            </Card>

            <Card className="border border-gray-200 bg-white shadow-sm">
              <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-4">
                <Users className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-lg font-semibold text-gray-800">Top Employee Sales</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {employeeChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={employeeChartData} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(value) => `$${Math.round(value / 1000)}k`} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="netSales" name="Net Sales" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                          {employeeChartData.map((entry, index) => (
                            <Cell key={entry.name} fill={EMPLOYEE_CHART_COLORS[index % EMPLOYEE_CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-gray-500">
                      {analytics?.meta?.employeeSalesAvailable === false
                        ? analytics?.meta?.employeeSalesNote
                        : "No employee sales data for this range"}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="mb-6 border border-gray-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">Employee Performance</CardTitle>
              <p className="text-sm text-gray-500">
                Net sales by server for {rangeLabel.toLowerCase()}, with labor hours from Toast.
                {analytics?.meta?.employeeSalesPartial && (
                  <span className="mt-1 block text-amber-600">{analytics.meta.employeeSalesNote}</span>
                )}
              </p>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0 sm:px-6 sm:pb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Employee</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Net Sales</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Orders</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Hours</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Sales / Hour</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Labor Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {employeePerformance.map((row) => (
                    <tr key={row.name} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-800">{row.name}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-emerald-700">
                        {formatCurrency(row.netSales)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">{formatNumber(row.orders)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">{formatNumber(row.hours)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                        {formatCurrency(row.salesPerHour)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                        {formatCurrency(row.laborCost)}
                      </td>
                    </tr>
                  ))}
                  {!employeePerformance.length && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">
                        No employee performance data for this range
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {orderSourceBreakdown.length > 0 && (
            <Card className="mb-6 border border-gray-200 bg-white shadow-sm">
              <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-4">
                <BarChart3 className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-lg font-semibold text-gray-800">Sales by Order Source</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={orderSourceBreakdown} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="displayName" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} width={70} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="sales" name="Net Sales" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                        {orderSourceBreakdown.map((entry, index) => (
                          <Cell key={entry.orderSource} fill={ORDER_SOURCE_COLORS[index % ORDER_SOURCE_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="mb-6 border border-gray-200 bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-800">
                Detailed Breakdown ({records.length} records)
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0 sm:px-6 sm:pb-6">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Restaurant</th>
                    {groupBy === "REVENUE_CENTER" && (
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Revenue Center</th>
                    )}
                    {groupBy === "DINING_OPTION" && (
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Dining Option</th>
                    )}
                    {groupBy === "ORDER_SOURCE" && (
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Order Source</th>
                    )}
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Net Sales</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Gross Sales</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Orders</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Guests</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Avg Order</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Discounts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {records.map((row, index) => (
                    <tr key={`${row.restaurantGuid}-${row.businessDate}-${index}`} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatBusinessDate(row.businessDate)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.restaurantName?.trim() || "—"}</td>
                      {groupBy === "REVENUE_CENTER" && (
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.revenueCenter || "—"}</td>
                      )}
                      {groupBy === "DINING_OPTION" && (
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.diningOption || "—"}</td>
                      )}
                      {groupBy === "ORDER_SOURCE" && (
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                          {formatOrderSourceLabel(row.orderSource)}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-emerald-700">
                        {formatCurrency(row.netSalesAmount)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                        {formatCurrency(row.grossSalesAmount)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">{formatNumber(row.ordersCount)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">{formatNumber(row.guestCount)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700">
                        {formatCurrency(row.avgOrderValue)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-rose-600">
                        {formatCurrency(row.discountAmount)}
                      </td>
                    </tr>
                  ))}
                  {!records.length && (
                    <tr>
                      <td colSpan={tableColSpan} className="px-4 py-10 text-center text-sm text-gray-500">
                        No sales records found for this selection
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
